import type { ReactNode } from 'react';
import {
  PageEyebrow,
  PageWideSection,
  Input,
  Select,
  SelectOption,
  TovVirtualGrid,
  formatEnumLabel,
  msg,
} from '@tov-plus/ui';

import {
  albumCoverUrl,
  buildAlbumCoverMap,
  galleryItemLabel,
  resolveGalleryAssetUrl,
  type GuestGalleryAlbum,
  type GuestGalleryItem,
} from './galleryHelpers';

interface GalleryPhotoGridProps {
  items: GuestGalleryItem[];
  favoriteItemIds: Set<string>;
  pendingFavoriteItemIds: Set<string>;
  showFavorites?: boolean;
  onOpen: (index: number) => void;
  onToggleFavorite: (itemId: string) => void;
}

export function GalleryPhotoGrid({
  items,
  favoriteItemIds,
  pendingFavoriteItemIds,
  showFavorites = true,
  onOpen,
  onToggleFavorite,
}: GalleryPhotoGridProps) {
  return (
    <TovVirtualGrid
      className="gallery-grid"
      estimateRowSize={() => 210}
      getKey={(item) => item.galleryItem.id}
      height="min(76vh, 58rem)"
      items={items}
      maxLanes={4}
      minItemWidth={160}
      renderItem={({ item, index }) => {
        const imageUrl = resolveGalleryAssetUrl(item.assetUrl);
        const label = galleryItemLabel(item);
        const isFavorite = favoriteItemIds.has(item.galleryItem.id);
        const isFavoritePending = pendingFavoriteItemIds.has(
          item.galleryItem.id,
        );

        return (
          <div
            className="gallery-grid__cell"
            key={item.galleryItem.id}
            role="listitem"
          >
            <button
              type="button"
              className="gallery-tile"
              onClick={() => onOpen(index)}
              aria-label={label}
            >
              {imageUrl ? (
                <img
                  className="gallery-tile__image"
                  src={imageUrl}
                  alt={label}
                  loading="lazy"
                />
              ) : (
                <span className="gallery-tile__fallback" aria-hidden="true" />
              )}
              {item.galleryItem.caption ? (
                <span className="gallery-tile__caption">
                  {item.galleryItem.caption}
                </span>
              ) : null}
            </button>
            {showFavorites ? (
              <button
                type="button"
                className={`gallery-tile__favorite${
                  isFavorite ? ' gallery-tile__favorite--active' : ''
                }`}
                aria-label={
                  isFavorite
                    ? msg('pages.gallery_page.gallery_remove_favorite')
                    : msg('pages.gallery_page.gallery_add_favorite')
                }
                disabled={isFavoritePending}
                onClick={() => onToggleFavorite(item.galleryItem.id)}
              >
                <span aria-hidden="true">{isFavorite ? '★' : '☆'}</span>
              </button>
            ) : null}
          </div>
        );
      }}
      role="list"
    />
  );
}

export type GalleryFeedFilter = 'approved' | 'my_uploads' | 'favorites';

interface GalleryControlsProps {
  activeFeed: GalleryFeedFilter;
  eventId: string;
  eventOptions: Array<{ id: string; displayName: string }>;
  favoriteCount: number;
  itemCount: number;
  searchQuery: string;
  showPersonalFeeds?: boolean;
  onFeedChange: (feed: GalleryFeedFilter) => void;
  onEventChange: (eventId: string) => void;
  onSearchChange: (query: string) => void;
}

export function GalleryControls({
  activeFeed,
  eventId,
  eventOptions,
  favoriteCount,
  itemCount,
  searchQuery,
  showPersonalFeeds = true,
  onFeedChange,
  onEventChange,
  onSearchChange,
}: GalleryControlsProps) {
  return (
    <PageWideSection className="gallery-controls">
      <div className="gallery-controls__feeds" role="group">
        <button
          type="button"
          className={`gallery-controls__feed${
            activeFeed === 'approved' ? ' gallery-controls__feed--active' : ''
          }`}
          onClick={() => onFeedChange('approved')}
        >
          {msg('pages.gallery_page.photos')}
        </button>
        {showPersonalFeeds ? (
          <>
            <button
              type="button"
              className={`gallery-controls__feed${
                activeFeed === 'my_uploads'
                  ? ' gallery-controls__feed--active'
                  : ''
              }`}
              onClick={() => onFeedChange('my_uploads')}
            >
              {msg('pages.gallery_page.my_photos')}
            </button>
            <button
              type="button"
              className={`gallery-controls__feed${
                activeFeed === 'favorites'
                  ? ' gallery-controls__feed--active'
                  : ''
              }`}
              onClick={() => onFeedChange('favorites')}
            >
              {msg('pages.gallery_page.favorites')} ({favoriteCount})
            </button>
          </>
        ) : null}
      </div>
      <label className="gallery-controls__event">
        <span>{msg('general.fields.event.label')}</span>
        <Select
          value={eventId}
          onChange={(event) => onEventChange(event.target.value)}
        >
          <SelectOption value="">
            {msg('pages.gallery_page.gallery_all_events')}
          </SelectOption>
          {eventOptions.map((option) => (
            <SelectOption key={option.id} value={option.id}>
              {option.displayName}
            </SelectOption>
          ))}
        </Select>
      </label>
      <label className="gallery-controls__event">
        <span>{msg('general.fields.search.label')}</span>
        <Input
          value={searchQuery}
          placeholder={msg('general.fields.search.placeholder')}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </label>
      <p className="gallery-controls__count">
        {itemCount} {msg('pages.gallery_page.gallery_photos_label')}
      </p>
    </PageWideSection>
  );
}

interface GalleryEmptyStateProps {
  canUpload: boolean;
  isFiltered: boolean;
  uploadAction?: ReactNode;
}

export function GalleryEmptyState({
  canUpload,
  isFiltered,
  uploadAction,
}: GalleryEmptyStateProps) {
  return (
    <div className="gallery-empty">
      <p className="gallery-empty__title">
        {isFiltered
          ? msg('pages.gallery_page.no_matching_photos')
          : msg('pages.gallery_page.gallery_no_photos_title')}
      </p>
      <p className="gallery-empty__body">
        {isFiltered
          ? msg('pages.gallery_page.no_photos_match_this_filter')
          : canUpload
            ? msg('pages.gallery_page.gallery_be_first_body')
            : msg('pages.gallery_page.gallery_no_photos_body')}
      </p>
      {canUpload && !isFiltered ? uploadAction : null}
    </div>
  );
}

interface MyUploadsPanelProps {
  myUploads: GuestGalleryItem[];
  onEditUpload: (item: GuestGalleryItem) => void;
}

export function MyUploadsPanel({
  myUploads,
  onEditUpload,
}: MyUploadsPanelProps) {
  if (myUploads.length === 0) {
    return null;
  }

  return (
    <PageWideSection>
      <PageEyebrow>{msg('pages.gallery_page.my_photos')}</PageEyebrow>
      <ul className="gallery-uploads">
        {myUploads.map((item) => {
          const imageUrl = resolveGalleryAssetUrl(item.assetUrl);

          return (
            <li className="gallery-uploads__item" key={item.galleryItem.id}>
              {imageUrl ? (
                <img
                  className="gallery-uploads__thumb"
                  src={imageUrl}
                  alt={galleryItemLabel(item)}
                  loading="lazy"
                />
              ) : (
                <span className="gallery-uploads__thumb gallery-tile__fallback" />
              )}
              <span
                className={`gallery-status gallery-status--${item.galleryItem.moderationStatus}`}
              >
                {galleryStatusLabel(item.galleryItem.moderationStatus)}
              </span>
              <span className="gallery-uploads__meta">
                <strong>{galleryItemLabel(item)}</strong>
                {item.asset.failureReason ? (
                  <span>
                    {msg(
                      'pages.gallery_upload_queue.photo_processing_needs_attention',
                    )}
                  </span>
                ) : item.asset.status === 'processing' ? (
                  <span>
                    {msg('pages.gallery_upload_queue.processing_photo')}
                  </span>
                ) : null}
                <span>
                  {item.event?.displayName ??
                    msg('pages.gallery_page.pages_gallery_page_occasion_wide')}
                </span>
                <span>
                  {msg('pages.gallery_page.gallery_submitted')}{' '}
                  {new Date(item.galleryItem.createdAt).toLocaleDateString()}
                </span>
                {item.galleryItem.moderationStatus === 'rejected' &&
                item.galleryItem.moderationNote ? (
                  <span>{item.galleryItem.moderationNote}</span>
                ) : null}
                <a
                  className="gallery-uploads__download"
                  download
                  href={item.downloadUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  {msg('pages.gallery_page.gallery_download_original')}
                </a>
                <button
                  type="button"
                  className="gallery-uploads__edit"
                  onClick={() => onEditUpload(item)}
                >
                  {msg('general.actions.edit')}
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </PageWideSection>
  );
}

interface AlbumsRailProps {
  totalItemCount?: number;
  albums: GuestGalleryAlbum[];
  items: GuestGalleryItem[];
  myUploads: GuestGalleryItem[];
  selectedAlbumId: string | null;
  onSelectAlbum: (albumId: string | null) => void;
}

export function AlbumsRail({
  totalItemCount,
  albums,
  items,
  myUploads,
  selectedAlbumId,
  onSelectAlbum,
}: AlbumsRailProps) {
  if (albums.length === 0) {
    return null;
  }

  const covers = buildAlbumCoverMap(items, myUploads);

  return (
    <PageWideSection>
      <PageEyebrow>
        {msg('pages.gallery_page.pages_gallery_page_albums')}
      </PageEyebrow>
      <ul className="gallery-albums">
        <li className="gallery-album">
          <button
            type="button"
            className={`gallery-album__button${
              selectedAlbumId === null ? ' gallery-album__button--active' : ''
            }`}
            onClick={() => onSelectAlbum(null)}
          >
            <span className="gallery-album__cover gallery-album__cover--all">
              {items.slice(0, 4).map((item) => {
                const coverUrl = resolveGalleryAssetUrl(item.assetUrl);

                return coverUrl ? (
                  <img
                    key={item.galleryItem.id}
                    src={coverUrl}
                    alt={galleryItemLabel(item)}
                    loading="lazy"
                  />
                ) : null;
              })}
            </span>
            <span className="gallery-album__meta">
              <strong>{msg('pages.gallery_page.photos')}</strong>
              <span>
                {totalItemCount ?? items.length}{' '}
                {msg('pages.gallery_page.gallery_photos_label')}
              </span>
            </span>
          </button>
        </li>
        {albums.map((entry) => {
          const cover = albumCoverUrl(entry, covers);

          return (
            <li className="gallery-album" key={entry.album.id}>
              <button
                type="button"
                className={`gallery-album__button${
                  selectedAlbumId === entry.album.id
                    ? ' gallery-album__button--active'
                    : ''
                }`}
                onClick={() => onSelectAlbum(entry.album.id)}
              >
                <span className="gallery-album__cover">
                  {cover ? (
                    <img src={cover} alt={entry.album.name} loading="lazy" />
                  ) : (
                    <span
                      className="gallery-tile__fallback"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span className="gallery-album__meta">
                  <strong>{entry.album.name}</strong>
                  <span>
                    {entry.itemCount ?? entry.items.length}{' '}
                    {msg('pages.gallery_page.gallery_photos_label')}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </PageWideSection>
  );
}

function galleryStatusLabel(
  status: GuestGalleryItem['galleryItem']['moderationStatus'],
) {
  switch (status) {
    case 'approved':
      return msg('pages.gallery_page.shared');
    case 'pending':
      return msg('pages.gallery_page.waiting_for_host_approval');
    case 'rejected':
      return msg('pages.gallery_page.not_shared');
    default:
      return formatEnumLabel(status);
  }
}
